import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Form } from 'antd';
import { DarwinInput } from 'components/darwin-ui';
import { DarwinModal } from 'components/darwin-ui/Modal';
import { useEffect, useState } from 'react';
import DarwinTypography from 'components/darwin-ui/Typography';
import styles from './DataCapture.module.css';

type SectionFieldEditModalProps = {
  fieldName: string;
  handleCloseModal: () => void;
  isModalOpen: boolean;
  defaultFieldName?: string;
  defaultHelpText?: string;
  originalFieldName?: string;
  originalHelpText?: string;
};

export const SectionFieldEditModal = ({
  fieldName,
  handleCloseModal,
  isModalOpen,
  defaultFieldName = '',
  defaultHelpText = '',
  originalFieldName = '',
  originalHelpText = '',
}: SectionFieldEditModalProps) => {
  const { t } = useTranslation();

  const { setValue, getValues } = useFormContext();

  const [editedField, setEditedField] = useState<{
    field_name: string;
    help_text: string;
  }>({
    field_name: '',
    help_text: '',
  });

  useEffect(() => {
    if (isModalOpen) {
      const currentFieldName = getValues(
        fieldName.replace('.field_value', '.field_name')
      );
      const currentHelpText = getValues(
        fieldName.replace('.field_value', '.help_text')
      );

      const isFieldNameEdited =
        currentFieldName && currentFieldName !== originalFieldName;
      const isHelpTextEdited =
        currentHelpText && currentHelpText !== originalHelpText;
      setEditedField({
        field_name: isFieldNameEdited ? currentFieldName : defaultFieldName,
        help_text: isHelpTextEdited ? currentHelpText : defaultHelpText,
      });
    }
  }, [
    isModalOpen,
    fieldName,
    getValues,
    defaultFieldName,
    defaultHelpText,
    originalFieldName,
    originalHelpText,
  ]);

  const handleSave = () => {
    setValue(
      fieldName.replace('.field_value', '.field_name'),
      editedField.field_name,
      { shouldDirty: true }
    );
    setValue(
      fieldName.replace('.field_value', '.help_text'),
      editedField.help_text,
      { shouldDirty: true }
    );
    handleCloseModal();
  };

  return (
    <DarwinModal
      open={isModalOpen}
      onCancel={handleCloseModal}
      onOk={handleSave}
      title={
        <DarwinTypography.Text className={styles.sectionFieldEditModalTitle}>
          {t('edit')}
        </DarwinTypography.Text>
      }
      cancelText={t('cancel')}
      okText={t('save')}
      centered
    >
      <Form layout="vertical">
        <Form.Item
          className={styles.sectionFieldEditModalInputLabel}
          label={t('edit-data-capture-form.edit-modal-field-name')}
        >
          <DarwinInput
            value={editedField.field_name}
            onChange={e =>
              setEditedField({ ...editedField, field_name: e.target.value })
            }
          />
        </Form.Item>
        <Form.Item
          className={styles.sectionFieldEditModalInputLabel}
          label={t('edit-data-capture-form.edit-modal-tooltip')}
        >
          <DarwinInput
            value={editedField.help_text}
            onChange={e =>
              setEditedField({ ...editedField, help_text: e.target.value })
            }
          />
        </Form.Item>
      </Form>
    </DarwinModal>
  );
};
